<script setup lang="ts">
  import { Floating, Spinner, SpinnerSize, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { useCloseOnEscape } from '@/entities/searchForm';
  import type { Country, Destination, Hotel, Resort } from '@/shared/model';
  import DestinationOption from './DestinationOption.vue';
  import { DESTINATION_TEXTS } from '../config/texts';
  import { destinationEntity } from '../lib/destinationEntity';
  import {
    getSelectedHotels,
    getSelectedResorts,
    getSelectedCountry,
    isCountry,
    type Selection,
  } from '../model/selection';
  import type { OptionGroup } from '../model/types';

  interface DestinationDropdownProps {
    options: OptionGroup[];
    selection: Selection;
    loading: boolean;
    showSelectAll: boolean;
    selectAllChecked: boolean;
    selectAllCountry?: Country | null;
    visible: boolean;
  }

  const props = withDefaults(defineProps<DestinationDropdownProps>(), {
    selectAllCountry: null,
  });

  interface DestinationDropdownEmits {
    (e: 'select', option: Destination): void;
    (e: 'select-all'): void;
    (e: 'close'): void;
  }

  const emit = defineEmits<DestinationDropdownEmits>();

  const hasItems = (groups: OptionGroup[]): boolean => {
    return groups.some((group) => group.items.length > 0);
  };

  const isItemChecked = (item: Destination): boolean => {
    if (item.type === 'resort') {
      return getSelectedResorts(props.selection).some((resort: Resort) => resort.id === item.resort.id);
    }
    if (item.type === 'hotel') {
      return getSelectedHotels(props.selection).some((hotel: Hotel) => hotel.id === item.hotel.id);
    }
    return isCountry(props.selection) && getSelectedCountry(props.selection)?.id === item.country.id;
  };

  const isLastOfGroup = (group: OptionGroup, index: number): boolean => {
    return index === group.items.length - 1;
  };

  const onFloatingHide = (): void => {
    if (props.visible) {
      emit('close');
    }
  };

  useCloseOnEscape(
    () => props.visible,
    () => emit('close'),
  );
</script>

<template>
  <Floating
    class="destination-dropdown__floating"
    :visible="visible"
    :disable-flip="true"
    :teleported="false"
    full-width
    @hide="onFloatingHide"
  >
    <template #reference>
      <slot name="reference" />
    </template>

    <template #floating>
      <div
        class="destination-dropdown"
        data-testid="destination-dropdown"
      >
        <DestinationOption
          v-if="showSelectAll && selectAllCountry"
          kind="selectAll"
          data-testid="destination-select-all"
          :option="selectAllCountry"
          :checked="selectAllChecked"
          @toggle="emit('select-all')"
        />

        <div
          v-if="loading"
          class="destination-dropdown__loading"
          data-testid="destination-loading"
        >
          <Spinner :size="SpinnerSize.S" />
        </div>

        <div
          v-else-if="!hasItems(options)"
          class="destination-dropdown__empty"
          data-testid="destination-nothing-found"
        >
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ DESTINATION_TEXTS.nothingFound }}
          </Typo>
        </div>

        <template v-else>
          <div
            v-for="group in options"
            :key="group.kind + '-' + (group.title ?? '')"
          >
            <Typo
              v-if="group.title"
              class="destination-dropdown__group-header"
              data-testid="destination-group-header"
              :variant="TypoVariant.BodyLMedium"
              tag="div"
            >
              {{ group.title }}
            </Typo>

            <DestinationOption
              v-for="(item, index) in group.items"
              :key="group.kind + '-' + destinationEntity(item).id"
              :option="destinationEntity(item)"
              :kind="group.kind"
              :checked="isItemChecked(item)"
              :last-of-type="isLastOfGroup(group, index)"
              @toggle="emit('select', item)"
            />
          </div>
        </template>
      </div>
    </template>
  </Floating>
</template>

<style scoped lang="scss">
  .destination-dropdown {
    background-color: var(--color-bg-primary);
    border-radius: var(--rounding-s);
    box-shadow: var(--shadow-s);
    box-sizing: border-box;
    max-height: 360px;
    min-width: 420px;
    overflow-y: auto;
    width: 100%;

    &__group-header {
      color: var(--color-text-primary);
      padding: var(--spacing-s) var(--spacing-m) var(--spacing-xxs);
    }

    &__loading {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: var(--spacing-l) var(--spacing-m);
    }

    &__empty {
      color: var(--color-text-secondary);
      padding: var(--spacing-l) var(--spacing-m);
      text-align: center;
    }
  }
</style>
